/**
 * Feature Access Context
 *
 * Provides feature access state management for the feature wall/paywall system.
 * This context fetches and stores the /access response which contains:
 * - Subscription tier and features
 * - Onboarding state (replaces get_onboarding)
 * - Employee limits
 * - User level (Owner/Manager/Employee)
 */

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
  ReactNode,
} from "react";
import { useAuth } from "./AuthContext";
import { useValueChurnProbe } from "../utils/loopDebug";
import { useApp } from "./AppContext";
import { inferEnvironment } from "../components/StaticWebGenerator/utils/previewUrlUtils";
import {
  FeatureAccessResponse,
  FeatureAccessError,
  FeatureKey,
  SubscriptionTier,
  BusinessVertical,
  TIER_CONFIG,
  FEATURE_TIER_MAP,
  SectionPermissions,
  SectionKey,
} from "../types/featureAccess";
import type { MessagingPlatform } from "../types/messaging";
import {
  adoptActingLocation,
  consumeActingLocationMoved,
  flagActingLocationMoved,
} from "../utils/actingLocation";
import { notifyWarning } from "../utils/notify";

/** Map camelCase feature keys from backend to snake_case used in frontend */
const FEATURE_KEY_MAP: Record<string, string> = {
  templatedWebsite: "templated_website",
  onlineBooking: "online_booking",
  calendar: "calendar",
  crm: "crm",
  inventory: "inventory",
  deposits: "deposits",
  report: "reports",
  reports: "reports",
  marketing: "marketing",
  payments: "payments",
  payroll: "payroll",
  basicAi: "basic_ai",
  aiAgents: "ai_agents",
  voiceAi: "voice_ai",
  loyalty: "loyalty",
  clientGroups: "client_groups",
};

/** Normalize /access API response to FeatureAccessResponse (handles backend key/format differences) */
export function normalizeAccessResponse(raw: any): FeatureAccessResponse {
  const tier: SubscriptionTier =
    raw.subscription_tier &&
    TIER_CONFIG[raw.subscription_tier as SubscriptionTier]
      ? (raw.subscription_tier as SubscriptionTier)
      : raw.subscription_status === "trial" || raw.trial_end_dt
        ? "trial"
        : "independent";
  const numActive = raw.number_of_active_employees ?? raw.active_employees ?? 0;
  const maxAllowed = raw.max_allowed_employees ?? 1;

  // Normalize feature keys from camelCase (backend) to snake_case (frontend)
  const rawUnlocked = raw.unlocked_features || {};
  const unlocked: Record<string, boolean> = {};
  for (const [key, value] of Object.entries(rawUnlocked)) {
    const normalizedKey = FEATURE_KEY_MAP[key] || key;
    unlocked[normalizedKey] = value as boolean;
  }

  // Pass through sectionPermissions as-is (backend uses camelCase which matches our types)
  const sectionPermissions: SectionPermissions | null =
    raw.sectionPermissions ?? null;

  // TODO: Voice AI is dev-only for now. Replace with a manual feature flag we
  // can toggle to enable the Voice AI nav item, page, and settings per
  // environment (and ultimately drive it from the backend /access response
  // like other features). See app/init-bookings-app/voice-ai/DEPLOYMENT.md
  // (in bookings-infra) for the cutover steps.
  if (unlocked["voice_ai"] === undefined) {
    unlocked["voice_ai"] = inferEnvironment() === "dev";
  }

  // Loyalty (Rewards) is driven by REACT_APP_ENABLE_LOYALTY until the
  // backend /access response emits unlocked_features.loyalty. All loyalty
  // surfaces hide entirely (no upsell) when this is false.
  if (unlocked["loyalty"] === undefined) {
    unlocked["loyalty"] = process.env.REACT_APP_ENABLE_LOYALTY === "true";
  }

  // Client Groups is a frontend-flagged rollout, default OFF: enable per
  // environment by setting REACT_APP_ENABLE_CLIENT_GROUPS=true in that env's
  // Netlify config (and .env.development for local), the same pattern as
  // loyalty. When the var is unset the flag is false and every surface hides.
  // The backend /access response can still override (line above).
  if (unlocked["client_groups"] === undefined) {
    unlocked["client_groups"] =
      process.env.REACT_APP_ENABLE_CLIENT_GROUPS === "true";
  }

  return {
    user_level: raw.user_level ?? "Owner",
    subscription_tier: tier,
    subscription_tier_level: TIER_CONFIG[tier]?.level ?? 0,
    trial_end_dt: raw.trial_end_dt ?? null,
    number_of_active_employees: numActive,
    max_allowed_employees: maxAllowed,
    onboarding: raw.onboarding ?? {
      initial_onboarding: "null",
      initial_onboarding_stage: null,
      progressive_employees: null,
      progressive_services: null,
      progressive_payments: null,
    },
    unlocked_features: unlocked as FeatureAccessResponse["unlocked_features"],
    section_permissions: sectionPermissions,
    can_add_employees: raw.can_add_employees ?? numActive < maxAllowed,
    employees_remaining:
      raw.employees_remaining ?? Math.max(0, maxAllowed - numActive),
    business_vertical: raw.business_vertical ?? null,
    staffing_model: raw.staffing_model ?? null,
    messaging_platform: raw.messaging_platform ?? null,
    // Multi-location fields (PR A / #399). Present only when the backend
    // MULTI_LOCATION_READS_ENABLED flag is ON; default to single-location
    // (empty list / no selection) when absent so the app stays inert with the
    // flag OFF.
    available_locations: Array.isArray(raw.available_locations)
      ? raw.available_locations
      : [],
    selected_salon_id: raw.selected_salon_id ?? null,
    selected_salon_uuid: raw.selected_salon_uuid ?? null,
  };
}

interface FeatureAccessContextType {
  /** The current access state from /access endpoint */
  access: FeatureAccessResponse | null;
  /** Whether access is currently being fetched */
  isLoading: boolean;
  /** Error state if access fetch failed */
  error: FeatureAccessError | null;
  /** Re-fetch access data; returns the new access (or null) so callers get fresh data without waiting for state update */
  refreshAccess: () => Promise<FeatureAccessResponse | null>;
  /** Check if a feature is unlocked */
  hasFeature: (featureKey: FeatureKey) => boolean;
  /** Check if user can add more employees */
  canAddEmployee: () => boolean;
  /** Get the minimum tier required for a feature */
  getRequiredTier: (featureKey: FeatureKey) => SubscriptionTier;
  /** Check if trial has expired */
  isTrialExpired: () => boolean;
  /** Get days until trial expires (null if not on trial) */
  daysUntilTrialExpires: () => number | null;
  /** Get display name for a tier */
  getTierDisplayName: (tier?: SubscriptionTier) => string;
  /** Get route to redirect to if onboarding is needed (null if complete) */
  getOnboardingRoute: () => string | null;
  /** Quick check if user needs any onboarding */
  needsOnboarding: () => boolean;
  /** Check if initial onboarding is complete */
  isInitialOnboardingComplete: () => boolean;
  /** Get section permissions for a specific section */
  getSectionPermissions: <K extends SectionKey>(
    section: K,
  ) => SectionPermissions[K] | null;
  /** Check a specific permission within a section (e.g. getSectionPermission('clients', 'canEdit')) */
  getSectionPermission: <K extends SectionKey>(
    section: K,
    permission: keyof SectionPermissions[K],
  ) => boolean;
  /** The salon's business vertical from /access (e.g. "hair", "med_spa") */
  businessVertical: BusinessVertical | null;
  /** This location's SMS/MMS platform from /access ("twilio" gates the Chat inbox). */
  messagingPlatform: MessagingPlatform | null;
}

const FeatureAccessContext = createContext<FeatureAccessContextType | null>(
  null,
);

interface FeatureAccessProviderProps {
  children: ReactNode;
}

export function FeatureAccessProvider({
  children,
}: FeatureAccessProviderProps) {
  const { isAuthenticated } = useAuth();
  const { getAccess } = useApp();

  const [access, setAccess] = useState<FeatureAccessResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<FeatureAccessError | null>(null);
  // True once this tab has booted on an /access payload — distinguishes a
  // mid-session refresh (location moved → reload) from the first load.
  const hasLoadedAccessRef = useRef(false);

  const refreshAccess =
    useCallback(async (): Promise<FeatureAccessResponse | null> => {
      if (!isAuthenticated) {
        hasLoadedAccessRef.current = false;
        setAccess(null);
        return null;
      }

      try {
        setIsLoading(true);
        setError(null);
        const response = await getAccess();
        const normalized = normalizeAccessResponse(response);
        if (
          adoptActingLocation(normalized.selected_salon_uuid) &&
          hasLoadedAccessRef.current
        ) {
          // The backend rejected this tab's location mid-session (assignment
          // revoked). Re-boot onto the location it landed on — the full
          // reload is the location-switch contract (useLocationSwitcher).
          // The notice is shown after the reload (see below).
          flagActingLocationMoved();
          window.location.reload();
          return null;
        }
        if (!hasLoadedAccessRef.current && consumeActingLocationMoved()) {
          notifyWarning(
            "You no longer have access to that location, so we've switched you to another one.",
            { title: "Location changed" },
          );
        }
        hasLoadedAccessRef.current = true;
        setAccess(normalized);
        return normalized;
      } catch (err: any) {
        console.error("Error fetching access:", err);

        // Determine error type
        let errorType: FeatureAccessError["type"] = "unknown";
        const status = err.status || err.errorBody?.status;
        const body = err.errorBody || err.body;

        if (status === 401) {
          errorType = "auth";
        } else if (status === 402) {
          errorType = "payment_required";
        } else if (status === 403) {
          if (body?.error === "feature_locked") {
            errorType = "feature_locked";
          } else if (body?.error === "employee_limit_reached") {
            errorType = "employee_limit";
          }
        } else if (err.message?.toLowerCase().includes("network")) {
          errorType = "network";
        }

        setError({
          type: errorType,
          message:
            body?.message ||
            err.message ||
            "Failed to fetch access permissions",
          details: body,
        });
        return null;
      } finally {
        setIsLoading(false);
      }
    }, [isAuthenticated, getAccess]);

  // Fetch access on auth change
  useEffect(() => {
    if (isAuthenticated) {
      refreshAccess();
    } else {
      setAccess(null);
      setError(null);
    }
  }, [isAuthenticated, refreshAccess]);

  const hasFeature = useCallback(
    (featureKey: FeatureKey): boolean => {
      if (!access) return false;
      return access.unlocked_features[featureKey] ?? false;
    },
    [access],
  );

  const canAddEmployee = useCallback((): boolean => {
    if (!access) return false;
    return access.can_add_employees;
  }, [access]);

  const getRequiredTier = useCallback(
    (featureKey: FeatureKey): SubscriptionTier => {
      return FEATURE_TIER_MAP[featureKey];
    },
    [],
  );

  const isTrialExpired = useCallback((): boolean => {
    if (!access) return false;
    if (access.subscription_tier !== "trial") return false;
    if (!access.trial_end_dt) return false;
    return new Date(access.trial_end_dt) < new Date();
  }, [access]);

  const daysUntilTrialExpires = useCallback((): number | null => {
    if (!access) return null;
    if (access.subscription_tier !== "trial") return null;
    if (!access.trial_end_dt) return null;

    const now = new Date();
    const trialEnd = new Date(access.trial_end_dt);
    const diffMs = trialEnd.getTime() - now.getTime();
    const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
    return Math.max(0, diffDays);
  }, [access]);

  const getTierDisplayName = useCallback(
    (tier?: SubscriptionTier): string => {
      if (!tier) {
        tier = access?.subscription_tier;
      }
      if (!tier) return "Unknown";
      return TIER_CONFIG[tier]?.displayName || tier;
    },
    [access],
  );

  // Determine routing based on onboarding state (replaces get_onboarding logic)
  const getOnboardingRoute = useCallback((): string | null => {
    if (!access) return null;
    const { onboarding } = access;

    // Initial onboarding not complete - go to onboarding wizard
    if (
      onboarding.initial_onboarding === "inprogress" ||
      onboarding.initial_onboarding === "null"
    ) {
      return "/onboarding";
    }

    // Initial complete - no redirect needed (progressive shows on dashboard)
    return null;
  }, [access]);

  // Quick check if user needs any onboarding
  const needsOnboarding = useCallback((): boolean => {
    if (!access) return false;
    const { onboarding } = access;

    // Initial onboarding not complete
    if (onboarding.initial_onboarding !== "complete") {
      return true;
    }

    // Progressive onboarding not complete
    const progressiveComplete =
      onboarding.progressive_employees === "complete" &&
      onboarding.progressive_services === "complete" &&
      onboarding.progressive_payments === "complete";

    return !progressiveComplete;
  }, [access]);

  // Check if initial onboarding is complete
  const isInitialOnboardingComplete = useCallback((): boolean => {
    if (!access) return false;
    return access.onboarding.initial_onboarding === "complete";
  }, [access]);

  // Get section permissions for a specific section
  const getSectionPermissions = useCallback(
    <K extends SectionKey>(section: K): SectionPermissions[K] | null => {
      if (!access?.section_permissions) return null;
      return access.section_permissions[section] ?? null;
    },
    [access],
  );

  // Check a specific permission within a section
  const getSectionPermission = useCallback(
    <K extends SectionKey>(
      section: K,
      permission: keyof SectionPermissions[K],
    ): boolean => {
      if (!access?.section_permissions) return false;
      const sectionPerms = access.section_permissions[section];
      if (!sectionPerms) return false;
      const value = sectionPerms[permission];
      return typeof value === "boolean" ? value : false;
    },
    [access],
  );
  // [LOOP] churn probe
  useValueChurnProbe("FeatureAccess", { access, isLoading, error });

  // Memoized (loop fix): the old inline object literal was a NEW identity on
  // every provider render, re-rendering every consumer even when nothing
  // changed. All function entries are useCallbacks keyed on [access] (and
  // refreshAccess on the now-stable getAccess), so this changes only when
  // access/isLoading/error actually change.
  const contextValue = useMemo<FeatureAccessContextType>(
    () => ({
      access,
      isLoading,
      error,
      refreshAccess,
      hasFeature,
      canAddEmployee,
      getRequiredTier,
      isTrialExpired,
      daysUntilTrialExpires,
      getTierDisplayName,
      getOnboardingRoute,
      needsOnboarding,
      isInitialOnboardingComplete,
      getSectionPermissions,
      getSectionPermission,
      businessVertical: access?.business_vertical ?? null,
      messagingPlatform: access?.messaging_platform ?? null,
    }),
    [
      access,
      isLoading,
      error,
      refreshAccess,
      hasFeature,
      canAddEmployee,
      getRequiredTier,
      isTrialExpired,
      daysUntilTrialExpires,
      getTierDisplayName,
      getOnboardingRoute,
      needsOnboarding,
      isInitialOnboardingComplete,
      getSectionPermissions,
      getSectionPermission,
    ],
  );

  return (
    <FeatureAccessContext.Provider value={contextValue}>
      {children}
    </FeatureAccessContext.Provider>
  );
}

export function useFeatureAccess() {
  const ctx = useContext(FeatureAccessContext);
  if (!ctx) {
    throw new Error(
      "useFeatureAccess must be used within FeatureAccessProvider",
    );
  }
  return ctx;
}
